import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { createFileRoute } from '@tanstack/react-router';
import { PASSWORD_RULES, signupRequestSchema, type SignupProfile } from '@siliconx/shared/dto/signup';
import { TOS_VERSION } from '@siliconx/shared';
import { API_BASE } from '../lib/api';
import { buildLoginUrl } from '../lib/auth';
import { mountTurnstile, resetTurnstile, TURNSTILE_CONFIGURED } from '../lib/turnstile';
import { SignupFrame } from '../components/shell/SignupFrame';
import { identify, track } from '@siliconx/shared/analytics';

/**
 * apps/customer/src/routes/signup.tsx
 *
 * SIL-1050: public individual/business self-signup from a referral link.
 * Renders OUTSIDE TosGate/IndividualProfileGate/auth (__root.tsx's
 * isPublicSignupPath) — a referral-link visitor has no Cognito session by
 * definition, so this route must never redirect to the Hosted UI.
 *
 * The code comes from the `#code=` URL FRAGMENT (never sent to any server,
 * never in CloudFront/API Gateway/Referer logs — design spec's "Code format
 * and hashing"), read directly off `window.location.hash` on mount rather
 * than a TanStack Router search param (a fragment is never part of the
 * router's own location object).
 *
 * Calls the public, unauthenticated /api/pub/signup/* endpoints directly
 * with `fetch` — never `authFetch`/the generated Hono client, both of which
 * assume a Cognito session and would redirect to the Hosted UI on a 401.
 *
 * Turnstile: two independent widgets (lib/turnstile.ts) — one for the
 * initial submit, one for resend — since a solved token is single-use and
 * can't be shared across the two calls. Neither ever sends an empty or
 * placeholder token: both submit buttons stay disabled until their own
 * widget resolves a real token, and the page shows an explicit
 * "temporarily unavailable" message instead of a form at all when
 * VITE_SIGNUP_TURNSTILE_SITE_KEY wasn't baked into this build.
 *
 * Prod bug fix (post-#1758/#1759/#1760): a password that failed the shared
 * SIGNUP_PASSWORD_REGEX rule got a 400 from the API, but handleSubmit
 * ignored the response status and always advanced to the verify step — the
 * visitor waited forever for an email that was never sent. This file now
 * (a) validates client-side against the SAME shared schema
 * (`signupRequestSchema`, `packages/shared/src/dto/signup.ts`) before ever
 * calling fetch, showing field-level messages and keeping submit blocked
 * until valid, and (b) branches on the response status for submit, confirm,
 * and resend instead of assuming success.
 *
 * SIL-1057: every step below renders inside `SignupFrame` (logo, wordmark,
 * card, sign-in/Terms/Privacy footer) — see that component's docstring for
 * why this isn't AppShell.
 *
 * SIL-1059: acceptance must be a forced, logged, affirmative action (the ToS
 * clickwrap gate design, docs/superpowers/specs/2026-09-10-tos-clickwrap-gate-
 * design.md) — not an auto-sent `tosVersion` with no user action. The form
 * step renders an unchecked-by-default required checkbox; its "Terms of
 * Service" label opens SignupFrame's SAME in-page terms panel via the
 * `openTerms` render-prop callback (SignupFrame's own docstring). The
 * request body only ever carries `tosAccepted: true`.
 *
 * SIL-1050 follow-up (prod UX bug): the submit button used to stay disabled
 * on `!formValid` with NO other indicator — while the password (or any
 * field) failed validation, `handleSubmit` never ran, so `fieldErrors` (set
 * only inside `handleSubmit`) could never populate and the visitor saw
 * nothing but a dead button. Fixed by (a) a live password-requirements
 * checklist under the password field, derived from the shared
 * `PASSWORD_RULES` (`packages/shared/src/dto/signup.ts`) so the rule isn't
 * duplicated here, (b) per-field validation on blur against the same shared
 * schema, clearing automatically once the value is valid, and (c) the submit
 * button disabling ONLY for `submitting` / Turnstile-not-ready / ToS-not-
 * accepted / password-rules-not-met — every other field's error still shows
 * on click (via `handleSubmit`, which moves focus to the first invalid
 * field) rather than gating the button, since only the ToS checkbox and the
 * password checklist give the visitor a live, always-visible signal before
 * they click Continue at all.
 *
 * 2026-09-20 live-site audit follow-up: the checklist's unmet-state text
 * (`text-muted-foreground`) already met WCAG AA contrast (see
 * styles/globals.css's `--n-400` comments — 5.8:1+ light, 6.4:1+ dark) but
 * read as visually "inert" at `text-xs`; bumped to `text-sm`. The Continue
 * button's disabled state used `disabled:opacity-50` over the same green as
 * its enabled state — a washed-out green that still looked clickable
 * rather than clearly off; disabled now renders in the neutral
 * `muted`/`muted-foreground` pair instead, and submitting shows a spinner +
 * "Creating account…" (previously text-only "Submitting…") so all three
 * button states (disabled/enabled/submitting) are visually distinct at a
 * glance, not just by cursor/opacity.
 */

type Step = 'loading' | 'invalid' | 'form' | 'verify' | 'success';

interface LookupResponse {
  organizationType?: 'individual' | 'business';
}

interface ZodIssue {
  path?: Array<string | number>;
  message?: string;
}

interface ApiErrorBody {
  success?: boolean;
  error?: { issues?: ZodIssue[]; name?: string } | string;
}

function readCodeFromFragment(): string | null {
  const hash = window.location.hash; // e.g. "#code=ABC123"
  const match = /(?:^|[#&])code=([^&]+)/.exec(hash);
  const raw = match?.[1];
  return raw ? decodeURIComponent(raw) : null;
}

async function postJson<T>(path: string, body: unknown): Promise<{ status: number; body: T }> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const parsed = (await res.json().catch(() => ({}))) as T;
  return { status: res.status, body: parsed };
}

/** Maps a Zod issue's path to this form's field-error keys. The API's
 * `password`/`email`/`tosAccepted` issues sit at the top level; profile
 * issues are nested under `profile.*`. Issues on fields this form doesn't
 * render (`code`, `tosVersion`, `turnstileToken`) are dropped — nothing on
 * the page can display them, and a client-valid submit should never
 * trigger them anyway. */
function fieldKeyForIssuePath(path: Array<string | number> | undefined): string | null {
  if (!path || path.length === 0) return null;
  const [head, ...rest] = path;
  if (head === 'profile' && rest.length > 0) return String(rest[rest.length - 1]);
  if (head === 'email' || head === 'password' || head === 'tosAccepted') return String(head);
  return null;
}

/** Field render order for the given org type — used to move focus to the
 * FIRST invalid field on a blocked submit (a visitor who can't see which
 * field is wrong is the whole bug this follow-up fixes). */
function fieldOrderFor(organizationType: 'individual' | 'business'): string[] {
  const profileFields = organizationType === 'individual' ? ['firstName', 'lastName'] : ['legalName', 'contactName'];
  return ['email', 'password', ...profileFields, 'countryOfResidence', 'tosAccepted'];
}

/** Extracts field-level messages from a 400 ZodError body
 * (`{"success":false,"error":{"issues":[...],"name":"ZodError"}}`, the
 * `hono/zod-validator` default shape). Returns null when the body isn't
 * that shape at all, so callers can fall back to a generic message. */
function fieldErrorsFromZodBody(body: unknown): Record<string, string> | null {
  const errorField = (body as ApiErrorBody | undefined)?.error;
  if (!errorField || typeof errorField === 'string' || !Array.isArray(errorField.issues))
    return null;
  const errors: Record<string, string> = {};
  for (const issue of errorField.issues) {
    const key = fieldKeyForIssuePath(issue.path);
    if (key && issue.message) errors[key] = issue.message;
  }
  return errors;
}

const GENERIC_RETRY_MESSAGE = 'Something went wrong. Please try again.';
const RATE_LIMITED_MESSAGE = 'Too many attempts. Try again later.';
const VERIFICATION_FAILED_MESSAGE = 'Verification failed. Please retry the check and submit again.';

export const Route = createFileRoute('/signup')({
  component: SignupPage,
});

export function SignupPage() {
  const [step, setStep] = useState<Step>('loading');
  const [code, setCode] = useState<string | null>(null);
  const [organizationType, setOrganizationType] = useState<'individual' | 'business' | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [contactName, setContactName] = useState('');
  const [countryOfResidence, setCountryOfResidence] = useState('US');
  // SIL-1059: the ToS clickwrap checkbox — unchecked by default, required.
  // Submit stays blocked (validate()) until this is true, and the request
  // body only ever includes `tosAccepted: true` when it is checked.
  const [tosAccepted, setTosAccepted] = useState(false);
  const [reservationSecret, setReservationSecret] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Two INDEPENDENT Turnstile widgets — SIL-1050: the initial submit
  // (handleSubmit) and every resend (handleResend) each need their own
  // fresh, single-use token; a solved token can't be reused across the two
  // calls. widget ids (for resetTurnstile after a resend) live in refs, not
  // state — they're imperative handles to the mounted widget, not render
  // inputs.
  const [submitTurnstileToken, setSubmitTurnstileToken] = useState('');
  const [resendTurnstileToken, setResendTurnstileToken] = useState('');
  const submitWidgetId = useRef<string | null>(null);
  const resendWidgetId = useRef<string | null>(null);

  // SIL-1050 follow-up: refs for moving focus to the first invalid field on
  // a blocked submit — a visitor who can't see WHICH field is wrong is the
  // bug this follow-up fixes.
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const legalNameRef = useRef<HTMLInputElement>(null);
  const contactNameRef = useRef<HTMLInputElement>(null);
  const countryRef = useRef<HTMLInputElement>(null);
  const tosRef = useRef<HTMLInputElement>(null);
  const fieldRefs: Record<string, React.RefObject<HTMLInputElement | null>> = {
    email: emailRef,
    password: passwordRef,
    firstName: firstNameRef,
    lastName: lastNameRef,
    legalName: legalNameRef,
    contactName: contactNameRef,
    countryOfResidence: countryRef,
    tosAccepted: tosRef,
  };

  useEffect(() => {
    const c = readCodeFromFragment();
    setCode(c);
    if (!c) {
      setStep('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      const { status, body } = await postJson<LookupResponse>('/api/pub/signup/lookup', {
        code: c,
      });
      if (cancelled) return;
      if (status === 200 && body.organizationType) {
        setOrganizationType(body.organizationType);
        setStep('form');
      } else {
        setStep('invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mounts the submit-step Turnstile widget once the form step is reachable
  // (its container only exists in the DOM once `step === 'form'`).
  useEffect(() => {
    if (step !== 'form') return;
    let cancelled = false;
    void mountTurnstile('sx-signup-turnstile-submit', (token) => {
      if (cancelled) return;
      setSubmitTurnstileToken(token);
    }).then((widgetId) => {
      if (!cancelled) submitWidgetId.current = widgetId;
    });
    return () => {
      cancelled = true;
    };
  }, [step]);

  // Mounts the resend-step Turnstile widget once the verify step is
  // reachable. Resend tokens are consumed one-at-a-time by handleResend,
  // which calls resetTurnstile() on this SAME widget instance afterward —
  // no re-mount needed since this effect only re-runs on a step change.
  useEffect(() => {
    if (step !== 'verify') return;
    let cancelled = false;
    void mountTurnstile('sx-signup-turnstile-resend', (token) => {
      if (cancelled) return;
      setResendTurnstileToken(token);
    }).then((widgetId) => {
      if (!cancelled) resendWidgetId.current = widgetId;
    });
    return () => {
      cancelled = true;
    };
  }, [step]);

  function buildProfile(): SignupProfile {
    return organizationType === 'individual'
      ? { organizationType: 'individual' as const, firstName, lastName, countryOfResidence }
      : { organizationType: 'business' as const, legalName, contactName, countryOfResidence };
  }

  /** Validates the current form state against the SHARED signup schema
   * (never a duplicated regex/rule set) and returns field-level messages.
   * `turnstileToken` is validated separately by the widget-token guard —
   * this function stands in a placeholder so a not-yet-solved widget never
   * shows as a form error. */
  function validate(): Record<string, string> {
    if (!code || !organizationType) return {};
    const parsed = signupRequestSchema.safeParse({
      code,
      email,
      password,
      profile: buildProfile(),
      tosVersion: TOS_VERSION,
      tosAccepted,
      turnstileToken: submitTurnstileToken || 'placeholder-pending-widget',
    });
    if (parsed.success) return {};
    const errors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const key = fieldKeyForIssuePath(issue.path);
      if (key && !errors[key]) errors[key] = issue.message;
    }
    return errors;
  }

  // SIL-1050 follow-up: as the visitor edits ANY field, drop any currently
  // shown message whose field is now valid — "clear the message when the
  // value becomes valid" without waiting for another blur or submit. This
  // never ADDS a message (that's handleFieldBlur / handleSubmit's job), only
  // removes ones that no longer apply.
  useEffect(() => {
    setFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const currentErrors = validate();
      const next: Record<string, string> = {};
      for (const key of Object.keys(prev)) {
        if (currentErrors[key]) next[key] = currentErrors[key];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password, firstName, lastName, legalName, contactName, countryOfResidence, tosAccepted]);

  /** Validates a single field against the shared schema when it loses focus
   * and shows its message — the minimum "field errors on blur" behavior.
   * Never clears (the effect above does that live as the value changes). */
  function handleFieldBlur(key: string): void {
    const errs = validate();
    if (errs[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: errs[key]! }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!code || !organizationType) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      const firstInvalidKey = fieldOrderFor(organizationType).find((key) => validationErrors[key]);
      if (firstInvalidKey) fieldRefs[firstInvalidKey]?.current?.focus();
      return;
    }
    setFieldErrors({});
    if (!submitTurnstileToken) return; // widget not solved yet — button stays disabled anyway

    setSubmitting(true);
    setErrorMessage(null);
    const { status, body } = await postJson<
      { status: string; reservationSecret?: string } & ApiErrorBody
    >('/api/pub/signup', {
      code,
      email,
      password,
      profile: buildProfile(),
      tosVersion: TOS_VERSION,
      // validate() already blocks submit unless tosAccepted is true, so this
      // is only ever sent as `true` — never a placeholder/false value.
      tosAccepted,
      turnstileToken: submitTurnstileToken,
    });
    setSubmitting(false);

    if (status === 202) {
      // Both a real secret and the uniform "no secret" 202 (the spec's
      // "never reveal" contract) advance to verify — the UX must not
      // distinguish them.
      setReservationSecret(body.reservationSecret ?? null);
      setStep('verify');
      return;
    }
    if (status === 400) {
      track('form_error', {
        form: 'signup',
        reason: 'validation',
        error_field: Object.keys(fieldErrorsFromZodBody(body) ?? {})[0],
      });
      const apiFieldErrors = fieldErrorsFromZodBody(body);
      if (apiFieldErrors && Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
      } else {
        setErrorMessage(GENERIC_RETRY_MESSAGE);
      }
      return;
    }
    if (status === 429) {
      track('form_error', { form: 'signup', reason: 'rate_limited' });
      setErrorMessage(RATE_LIMITED_MESSAGE);
      return;
    }
    if (status === 503) {
      track('form_error', { form: 'signup', reason: 'turnstile' });
      setErrorMessage(VERIFICATION_FAILED_MESSAGE);
      setSubmitTurnstileToken('');
      resetTurnstile(submitWidgetId.current);
      return;
    }
    track('form_error', { form: 'signup', reason: 'api', error_code: String(status) });
    setErrorMessage(GENERIC_RETRY_MESSAGE);
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!code || !reservationSecret) return;
    setSubmitting(true);
    setErrorMessage(null);
    const { status } = await postJson('/api/pub/signup/confirm', {
      code,
      email,
      password,
      verificationCode,
      reservationSecret,
    });
    setSubmitting(false);
    if (status === 201) {
      if (organizationType === 'business' || organizationType === 'individual') {
        track('sign_up', { organization_type: organizationType });
      }
      identify(email);
      setStep('success');
      return;
    }
    if (status === 400) {
      track('form_error', { form: 'signup_confirm', reason: 'validation', error_field: 'verificationCode' });
      setErrorMessage('That code did not work. Check it and try again, or resend.');
      return;
    }
    if (status === 429) {
      track('form_error', { form: 'signup_confirm', reason: 'rate_limited' });
      setErrorMessage(RATE_LIMITED_MESSAGE);
      return;
    }
    if (status === 503) {
      track('form_error', { form: 'signup_confirm', reason: 'api', error_code: String(status) });
      setErrorMessage(VERIFICATION_FAILED_MESSAGE);
      return;
    }
    track('form_error', { form: 'signup_confirm', reason: 'api', error_code: String(status) });
    setErrorMessage(GENERIC_RETRY_MESSAGE);
  }

  async function handleResend() {
    if (!code || !reservationSecret || !resendTurnstileToken) return;
    setSubmitting(true);
    setResendNotice(null);
    const { status } = await postJson('/api/pub/signup/resend', {
      code,
      email,
      password,
      reservationSecret,
      turnstileToken: resendTurnstileToken,
    });
    setSubmitting(false);
    if (status === 202) {
      setResendNotice('Code sent.');
    } else if (status === 429) {
      setErrorMessage(RATE_LIMITED_MESSAGE);
    } else if (status === 503) {
      setErrorMessage(VERIFICATION_FAILED_MESSAGE);
    } else {
      setErrorMessage(GENERIC_RETRY_MESSAGE);
    }
    // Turnstile tokens are single-use — clear it and reset the widget so
    // the NEXT resend (or a stray double-click) never resubmits a spent
    // token; mountTurnstile's callback repopulates it once solved again.
    setResendTurnstileToken('');
    resetTurnstile(resendWidgetId.current);
  }

  return (
    <SignupFrame>
      {({ openTerms }) => (
        <>
      {step === 'loading' && (
        <p className="text-center text-sm text-muted-foreground">Checking your link…</p>
      )}

      {step === 'invalid' && (
        <div className="space-y-4 text-center">
          <h1 className="text-lg font-semibold">This link is invalid or expired.</h1>
        </div>
      )}

      {step === 'form' && organizationType && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <h1 className="text-lg font-semibold">Create your SiliconX account</h1>
          <div>
            <input
              ref={emailRef}
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => handleFieldBlur('email')}
              aria-invalid={Boolean(fieldErrors.email)}
              className="w-full rounded-md border px-3 py-2"
            />
            {fieldErrors.email && <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>}
          </div>
          <div>
            <input
              ref={passwordRef}
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleFieldBlur('password')}
              aria-invalid={Boolean(fieldErrors.password)}
              aria-describedby="signup-password-requirements"
              className="w-full rounded-md border px-3 py-2"
            />
            {fieldErrors.password && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
            )}
            {/* SIL-1050 follow-up: live checklist, not just a disabled button
                with no explanation. aria-live announces each state change to
                screen readers as the visitor types; met/not-met is conveyed
                by icon + text, not color alone. */}
            <ul
              id="signup-password-requirements"
              aria-live="polite"
              data-testid="password-requirements"
              className="mt-1.5 space-y-0.5 text-sm"
            >
              {PASSWORD_RULES.map((rule) => {
                const met = rule.test(password);
                return (
                  <li
                    key={rule.key}
                    data-testid={`password-rule-${rule.key}`}
                    data-met={met}
                    className={`flex items-center gap-1.5 ${met ? 'text-green-700' : 'text-muted-foreground'}`}
                  >
                    <span aria-hidden="true">{met ? '✓' : '○'}</span>
                    <span>
                      {rule.label}
                      <span className="sr-only">{met ? ' — met' : ' — not met'}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          {organizationType === 'individual' ? (
            <>
              <div>
                <input
                  ref={firstNameRef}
                  required
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onBlur={() => handleFieldBlur('firstName')}
                  aria-invalid={Boolean(fieldErrors.firstName)}
                  className="w-full rounded-md border px-3 py-2"
                />
                {fieldErrors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.firstName}</p>
                )}
              </div>
              <div>
                <input
                  ref={lastNameRef}
                  required
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  onBlur={() => handleFieldBlur('lastName')}
                  aria-invalid={Boolean(fieldErrors.lastName)}
                  className="w-full rounded-md border px-3 py-2"
                />
                {fieldErrors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.lastName}</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <input
                  ref={legalNameRef}
                  required
                  placeholder="Legal business name"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  onBlur={() => handleFieldBlur('legalName')}
                  aria-invalid={Boolean(fieldErrors.legalName)}
                  className="w-full rounded-md border px-3 py-2"
                />
                {fieldErrors.legalName && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.legalName}</p>
                )}
              </div>
              <div>
                <input
                  ref={contactNameRef}
                  required
                  placeholder="Contact name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  onBlur={() => handleFieldBlur('contactName')}
                  aria-invalid={Boolean(fieldErrors.contactName)}
                  className="w-full rounded-md border px-3 py-2"
                />
                {fieldErrors.contactName && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.contactName}</p>
                )}
              </div>
            </>
          )}
          <div>
            <input
              ref={countryRef}
              required
              placeholder="Country (2-letter code)"
              value={countryOfResidence}
              onChange={(e) => setCountryOfResidence(e.target.value.toUpperCase())}
              onBlur={() => handleFieldBlur('countryOfResidence')}
              aria-invalid={Boolean(fieldErrors.countryOfResidence)}
              maxLength={2}
              className="w-full rounded-md border px-3 py-2"
            />
            {fieldErrors.countryOfResidence && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.countryOfResidence}</p>
            )}
          </div>
          {TURNSTILE_CONFIGURED ? (
            <div id="sx-signup-turnstile-submit" data-testid="turnstile-submit-mount" />
          ) : (
            <p className="text-sm text-red-600" data-testid="turnstile-unconfigured">
              Signup is temporarily unavailable. Please try again later.
            </p>
          )}
          <div>
            <label className="flex items-start gap-2 text-sm">
              <input
                ref={tosRef}
                type="checkbox"
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
                onBlur={() => handleFieldBlur('tosAccepted')}
                aria-invalid={Boolean(fieldErrors.tosAccepted)}
                className="mt-0.5"
              />
              <span>
                <span className="block">
                  I agree to the SiliconX{' '}
                  <button
                    type="button"
                    onClick={openTerms}
                    aria-label="Show ToS clickwrap text"
                    className="text-brand-600 underline"
                  >
                    Terms of Service
                  </button>
                </span>
                <span className="block text-xs text-muted-foreground">(version {TOS_VERSION})</span>
              </span>
            </label>
            {fieldErrors.tosAccepted && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors.tosAccepted}</p>
            )}
          </div>
          {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
          <button
            type="submit"
            disabled={
              submitting ||
              !TURNSTILE_CONFIGURED ||
              !submitTurnstileToken ||
              !tosAccepted ||
              !PASSWORD_RULES.every((rule) => rule.test(password))
            }
            aria-busy={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-4 py-2 font-medium text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          >
            {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            {submitting ? 'Creating account…' : 'Continue'}
          </button>
          {/* SIL-1050 follow-up: the button stays clickable for field issues
              that only reveal themselves on blur/submit (email format, name,
              country) — handleSubmit shows those field errors on click.
              ToS and password have their own always-visible indicators (the
              checkbox itself, the live checklist above), so the button gates
              on those two directly instead of requiring a click to find out;
              otherwise the ONLY reason it's disabled once the form step has
              loaded is a not-yet-solved Turnstile widget — explain that. */}
          {TURNSTILE_CONFIGURED && tosAccepted && PASSWORD_RULES.every((rule) => rule.test(password)) && !submitTurnstileToken && !submitting && (
            <p className="text-center text-xs text-muted-foreground" data-testid="turnstile-pending-hint">
              Complete the verification check above to continue.
            </p>
          )}
        </form>
      )}

      {step === 'verify' && (
        <form onSubmit={handleConfirm} className="space-y-4">
          <h1 className="text-lg font-semibold">Verify your email</h1>
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code we sent to {email}.
          </p>
          <input
            required
            placeholder="6-digit code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            maxLength={6}
            className="w-full rounded-md border px-3 py-2"
          />
          {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
          {resendNotice && <p className="text-sm text-muted-foreground">{resendNotice}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-brand-600 px-4 py-2 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {submitting ? 'Verifying…' : 'Verify'}
          </button>
          {TURNSTILE_CONFIGURED ? (
            <div id="sx-signup-turnstile-resend" data-testid="turnstile-resend-mount" />
          ) : (
            <p className="text-sm text-red-600" data-testid="turnstile-unconfigured">
              Resending is temporarily unavailable. Please try again later.
            </p>
          )}
          <button
            type="button"
            onClick={handleResend}
            disabled={submitting || !TURNSTILE_CONFIGURED || !resendTurnstileToken}
            className="w-full text-sm text-brand-600 underline disabled:opacity-50"
          >
            Resend code
          </button>
          <p className="text-center text-xs text-muted-foreground">
            Didn&apos;t get a code? Contact your SiliconX representative.
          </p>
        </form>
      )}

      {step === 'success' && (
        <div className="space-y-4 text-center">
          <h1 className="text-lg font-semibold">You&apos;re all set.</h1>
          <button
            type="button"
            onClick={async () => {
              const url = await buildLoginUrl();
              window.location.assign(url);
            }}
            className="w-full rounded-md bg-brand-600 px-4 py-2 text-white hover:bg-brand-700"
          >
            Sign in
          </button>
        </div>
      )}
        </>
      )}
    </SignupFrame>
  );
}
